import React from "react";
import { useScrollLock } from "../lib/scrollLock";
import styles from "./AlertModal.module.css";

export type AlertModalType = "success" | "error" | "info";

export interface AlertModalProps {
  visible: boolean;
  type?: AlertModalType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onClose?: () => void;
}

function SuccessIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--accent, #ff6b6b)" }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

export default function AlertModal({
  visible,
  type = "info",
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  onClose,
}: AlertModalProps) {
  useScrollLock(visible);

  if (!visible) return null;

  const handleClose = () => {
    if (onClose) onClose();
    else if (onConfirm) onConfirm();
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="presentation"
    >
      <div
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-modal-title"
      >
        <div className={`${styles.iconContainer} ${styles[type]}`}>
          {type === "success" && <SuccessIcon />}
          {type === "error" && <ErrorIcon />}
          {type === "info" && <InfoIcon />}
        </div>

        <h3 id="alert-modal-title" className={styles.title}>
          {title}
        </h3>
        <p className={styles.message}>{message}</p>

        <div className={styles.buttonRow}>
          {onConfirm && onClose && (
            <button
              type="button"
              className={`${styles.button} ${styles.cancelButton}`}
              onClick={onClose}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className={`${styles.button} ${styles.confirmButton} ${styles[`confirm_${type}`]}`}
            onClick={onConfirm || onClose || handleClose}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
